// filters an ordered list of strings values out of an
//      ordered list of string values in O(n)
// requires every value in toRemove to be in orig and
//    and for both lists to be sets
export const filterSortedList = (orig, toRemove) => {
  let i = 0;
  return orig.reduce((ret, x) => {
    if (x === toRemove[i]) {
      i++;
      return ret;
    }
    return [...ret, x];
  }, []);
};

// insert a value into an ordered list of values without allowing duplicates
export const insertSortedSet = (arr, el) => {
  let ret = arr;
  if (el.localeCompare(arr[0]) === -1) {
    ret = [el, ...arr];
  }
  for (let i = 0; i < arr.length; i++) {
    const cmp = el.localeCompare(arr[i]);
    if (cmp === 1) {
      ret = [...arr.slice(0, i + 1), el, ...arr.slice(i + 1)];
    } else if (cmp === 0) {
      return arr;
    }
  }
  return ret;
};

// converts list of numbers to list of continuous ranges
export const convertToRanges = (nums) => {
  const ranges = [[nums[0], nums[0]]];
  let j = 0;

  for (let i = 1; i < nums.length; i++) {
    if (nums[i] - ranges[j][1] === 1) {
      ranges[j][1] = nums[i];
    } else {
      ranges[++j] = [nums[i], nums[i]];
    }
  }
  return ranges
    .map(([start, end]) => (start !== end ? `${start}-${end}` : `${start}`))
    .join(", ");
};

const wellToNum = (well) =>
  (well.charCodeAt(0) - 64) * 100 + parseInt(well.substring(1));

const numToWell = (num) => {
  const row = Math.floor(num / 100);
  const col = num % 100;
  if (col < 10) {
    return String.fromCharCode(row + 64) + "0" + col;
  }
  return String.fromCharCode(row + 64) + col;
};

export const convertWellsToRanges = (wells) => {
  const nums = wells.map(wellToNum);
  const ranges = [[nums[0], nums[0]]];
  let j = 0;

  for (let i = 1; i < nums.length; i++) {
    if (nums[i] - ranges[j][1] === 1) {
      ranges[j][1] = nums[i];
    } else {
      ranges[++j] = [nums[i], nums[i]];
    }
  }
  return ranges
    .map(([start, end]) =>
      start !== end
        ? `${numToWell(start)}-${numToWell(end)}`
        : `${numToWell(start)}`
    )
    .join(", ");
};

export const addDefaultOpt = (obj) => [
  {
    value: "",
    label: "",
  },
  ...obj,
];

export const getVectorTypeGivenRegion = (region) => {
  return region?.startsWith("moz") || region?.startsWith("mos")
    ? "mosquito"
    : region?.startsWith("tick")
    ? "tick"
    : "";
};

export const caseInsensitiveIteree = (key) => (item) =>
  Array.isArray(item?.[key]) &&
  item[key].every((x) => typeof x === "string" || x instanceof String)
    ? item[key].sort().join(", ").toLowerCase()
    : typeof item?.[key] === "string" || item?.[key] instanceof String
    ? item[key].toLowerCase()
    : item?.[key];

// Region key -> display name for one alg version's vectorTypes, as returned by
// /algVersions/vectorTypes. Flattened across vector types since region keys are
// unique across mosquito and tick. The "<vectorType>: all" entry is the
// selector's label for the whole model family, not a region name, so it is
// left out.
const flattenRegionNames = (vectorTypes) =>
  Object.values(vectorTypes || {}).reduce((names, regions) => {
    if (regions && typeof regions === "object" && !Array.isArray(regions)) {
      Object.entries(regions).forEach(([region, name]) => {
        if (name && name !== "all") {
          names[region] = name;
        }
      });
    }
    return names;
  }, {});

// Raw data rows carry the region the alg service recorded, which is the
// region key (e.g. a uuid) whenever the manifest had no display name for it at
// capture. Resolve it the way the Region selector does - the row's own alg
// version first, then the latest - and keep the value as-is when neither
// knows it.
export const withRegionDisplayNames = (rows, algVersions) => {
  if (!Array.isArray(rows)) {
    return rows;
  }
  const latest = flattenRegionNames(algVersions?.vectorTypes);
  const byVersion = {};
  return rows.map((row) => {
    const region = row?.algRegionName;
    if (!region) {
      return row;
    }
    const version = row.algVersion;
    if (version && !(version in byVersion)) {
      byVersion[version] = flattenRegionNames(algVersions?.[version]);
    }
    const name = byVersion[version]?.[region] || latest[region];
    return name && name !== region ? { ...row, algRegionName: name } : row;
  });
};
